import camelCase from 'camelcase'
import { IntentType } from './segmentation-model'
import { RealEstateCategory, SegmentationType, TransactionType } from '../../../@types/types'
import { API_ENDPOINT } from '../../../config'

/**
 * Constructs the 'resolve' URL for a given intent and category to redirect to the CLB.
 * The CLB will:
 * - Report new insertions to the datalake.
 * - Redirect unsupported flows to their matching OMA flows
 * - Else it will redirect to CL app again with the proper flow inside the url
 * We are not directly navigating inside the CL to the proper flow because the datalake and OMA stuff
 * would need to be done in CL as well.
 */
export const resolveEndpoint = (
  segmentationType: SegmentationType,
  transactionType: TransactionType | 'SHORT_TERM_ACCOMMODATION',
  category: RealEstateCategory
) => {
  return `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/segments/resolve${path(
    segmentationType,
    transactionType,
    category
  )}?entryMode=CENTRAL_INSERTION_START_PAGE`
}

function path(
  segmentationType: SegmentationType,
  transactionType: TransactionType | 'SHORT_TERM_ACCOMMODATION',
  category: RealEstateCategory
): string {
  // TENANT always uses /tenant/rent/ path
  if (segmentationType === SegmentationType.TENANT) {
    return category === RealEstateCategory.FLAT_SHARE_ROOM
      ? '/tenant/rent/flatShareRoom'
      : `/tenant/rent/${categoryToString(category)}`
  }

  // AGENT uses /agent/ path
  if (segmentationType === SegmentationType.AGENT) {
    if (transactionType === TransactionType.RENT || transactionType === 'SHORT_TERM_ACCOMMODATION') {
      return category === RealEstateCategory.FLAT_SHARE_ROOM
        ? '/agent/rent/flatShareRoom'
        : `/agent/rent/${categoryToString(category)}`
    } else {
      return `/agent/sell/${categoryToString(category)}`
    }
  }

  // PROPERTY_OWNER uses /propertyOwner/ path
  if (transactionType === TransactionType.RENT || transactionType === 'SHORT_TERM_ACCOMMODATION') {
    return `/propertyOwner/rent/${categoryToString(category)}`
  } else {
    return `/propertyOwner/sell/${categoryToString(category)}`
  }
}

// Legacy function for backward compatibility
export const resolveEndpointLegacy = (intent: IntentType, category: RealEstateCategory) => {
  return `${API_ENDPOINT}/anbieten/private-anbieter/inserieren/api/segments/resolve${pathLegacy(
    intent,
    category
  )}?entryMode=CENTRAL_INSERTION_START_PAGE`
}

function pathLegacy(intent: IntentType, category: RealEstateCategory): string | undefined {
  switch (intent) {
    case IntentType.TENANT:
      return category === RealEstateCategory.FLAT_SHARE_ROOM
        ? '/tenant/rent/flatShareRoom'
        : `/propertyOwner/rent/${categoryToString(category)}`

    case IntentType.BUYER:
      return `/propertyOwner/sell/${categoryToString(category)}`

    case IntentType.NEXT_TENANT:
      return `/tenant/rent/${categoryToString(category)}`
  }
}

function categoryToString(category: RealEstateCategory): string {
  switch (category) {
    case RealEstateCategory.GASTRONOMY:
      return 'hotel'
    case RealEstateCategory.HANGAR:
      return 'hall'
    case RealEstateCategory.STORE:
      return 'retailIndustry'
    case RealEstateCategory.INVESTMENT:
      return 'investmentEstate'
    default:
      return camelCase(category)
  }
}
